import { useState, useMemo, createContext, useContext } from 'react';
import { Wand2, Paintbrush, Copy, Undo2, Redo2, Zap, History } from 'lucide-react';
import { useSchedules } from '../../hooks/useSchedules';
import { useAgents } from '../../hooks/useAgents';
import { useSchedulingActions } from '../../hooks/useSchedulingActions';
import { useAuthStore } from '../../stores/auth.store';
import DogLoader from '../../components/ui/DogLoader';
import StaffingCoverageChart from '../../components/schedules/StaffingCoverageChart';
import MetricsRow from '../../components/schedules/MetricsRow';
import AgentTimeline from '../../components/schedules/AgentTimeline';
import { ToastContainer, ToastProps } from '../../components/ui/Toast';
import VersionHistorySidebar from '../../components/modals/VersionHistorySidebar';

// Types
export type ViewMode = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export type ChannelFilter = 'TOTAL' | 'PHONE' | 'CHAT' | 'EMAIL';

export interface FilterCriteria {
  search: string;
  teams: string[];
  skills: string[];
  channels: string[];
  statuses: string[];
  availability?: {
    hour: number;
    channel: string;
  };
}

export type ActivityType = 'PHONE' | 'CHAT' | 'EMAIL' | 'MEAL' | 'BREAK' | 'COACHING' | 'TRAINING' | 'MEETING';

export interface SchedulingContextValue {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  cursorPosition: number | null;
  setCursorPosition: (position: number | null) => void;
  selectedAgents: string[];
  setSelectedAgents: (agents: string[]) => void;
  blockPainterActive: boolean;
  setBlockPainterActive: (active: boolean) => void;
  painterActivityType: ActivityType | null;
  setPainterActivityType: (activity: ActivityType | null) => void;
  filterCriteria: FilterCriteria;
  setFilterCriteria: (criteria: FilterCriteria) => void;
  channelFilter: ChannelFilter;
  setChannelFilter: (channel: ChannelFilter) => void;
  teamFilter: string;
  setTeamFilter: (team: string) => void;
  shiftFilter: string;
  setShiftFilter: (shift: string) => void;
  scheduleId: string | null;
  showToast: (message: string, type: 'error' | 'warning' | 'success' | 'info') => void;
  // New scheduling actions
  canUndo: boolean;
  canRedo: boolean;
  hasCopiedIntervals: boolean;
  copiedIntervalsCount: number;
  handleUndo: () => void;
  handleRedo: () => void;
  handleCopyWeek: () => void;
  // Version history
  openVersionHistory: () => void;
}

// Context for sharing state across scheduling components
const SchedulingContext = createContext<SchedulingContextValue | null>(null);

export function useSchedulingContext() {
  const context = useContext(SchedulingContext);
  if (!context) {
    throw new Error('useSchedulingContext must be used within SchedulingCanvas');
  }
  return context;
}

export default function SchedulingCanvas() {
  const user = useAuthStore((state) => state.user);
  const { data: schedules, isLoading: schedulesLoading } = useSchedules(user?.organizationId);
  const { isLoading: agentsLoading } = useAgents();

  // Selected schedule state
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | undefined>();

  // Get the selected schedule or default to most recent
  const currentSchedule = useMemo(() => {
    if (selectedScheduleId) {
      return schedules?.find(s => s.id === selectedScheduleId) || schedules?.[0];
    }
    return schedules?.[0];
  }, [schedules, selectedScheduleId]);

  const scheduleId = currentSchedule?.id || null;

  // View and navigation state
  const [viewMode, setViewMode] = useState<ViewMode>('DAILY');
  // Default to December 30, 2025 where seed data exists
  const [selectedDate, setSelectedDate] = useState(() => {
    const defaultDate = new Date('2025-12-30');
    defaultDate.setHours(0, 0, 0, 0);
    return defaultDate;
  });
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('TOTAL');
  const [teamFilter, setTeamFilter] = useState<string>('ALL');
  const [shiftFilter, setShiftFilter] = useState<string>('ALL');

  // Interaction state
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [blockPainterActive, setBlockPainterActive] = useState(false);
  const [painterActivityType, setPainterActivityType] = useState<ActivityType | null>(null);
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({
    search: '',
    teams: [],
    skills: [],
    channels: [],
    statuses: [],
  });

  // Toast state
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  // Version history state
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const showToast = (message: string, type: 'error' | 'warning' | 'success' | 'info') => {
    const id = `toast-${Date.now()}`;
    setToasts((prev) => [...prev, { id, message, type, onClose: () => removeToast(id) }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Scheduling actions (undo/redo/copy/paste)
  const schedulingActions = useSchedulingActions(scheduleId, selectedDate);

  const handleUndo = () => {
    const action = schedulingActions.undo();
    if (action) {
      showToast('Undone', 'info');
    }
  };

  const handleRedo = () => {
    const action = schedulingActions.redo();
    if (action) {
      showToast('Redone', 'info');
    }
  };

  const handleCopyWeek = () => {
    // Copy entire week of schedule
    const nextWeekDate = new Date(selectedDate);
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    setSelectedDate(nextWeekDate);
    showToast('Schedule copied to next week', 'success');
  };

  // Context value
  const contextValue: SchedulingContextValue = {
    viewMode,
    setViewMode,
    selectedDate,
    setSelectedDate,
    cursorPosition,
    setCursorPosition,
    selectedAgents,
    setSelectedAgents,
    blockPainterActive,
    setBlockPainterActive,
    painterActivityType,
    setPainterActivityType,
    filterCriteria,
    setFilterCriteria,
    channelFilter,
    setChannelFilter,
    teamFilter,
    setTeamFilter,
    shiftFilter,
    setShiftFilter,
    scheduleId,
    showToast,
    // Scheduling actions
    canUndo: schedulingActions.canUndo,
    canRedo: schedulingActions.canRedo,
    hasCopiedIntervals: schedulingActions.hasCopiedIntervals,
    copiedIntervalsCount: schedulingActions.copiedIntervalsCount,
    handleUndo,
    handleRedo,
    handleCopyWeek,
    // Version history
    openVersionHistory: () => setIsVersionHistoryOpen(true),
  };

  // Loading state
  if (schedulesLoading || agentsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <DogLoader />
      </div>
    );
  }

  // No schedule state
  if (!currentSchedule) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Schedule Found</h2>
          <p className="text-gray-600">Create your first schedule to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <SchedulingContext.Provider value={contextValue}>
      <div className="flex flex-col h-screen bg-gray-50">
        {/* Header */}
        <Header
          schedules={schedules}
          currentSchedule={currentSchedule}
          setSelectedScheduleId={setSelectedScheduleId}
        />

        {/* Main Content - 3 Section Layout */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Staffing Coverage Chart - Top Section */}
          <div className="bg-white border-b border-gray-200 flex-shrink-0">
            <StaffingCoverageChart />
          </div>

          {/* Metrics Row - Middle Section */}
          <div className="bg-white border-b border-gray-200 flex-shrink-0">
            <MetricsRow />
          </div>

          {/* Agent Timeline - Bottom Section (scrollable) - minimum height ensures visibility */}
          <div className="flex-1 overflow-hidden min-h-[400px]">
            <AgentTimeline />
          </div>
        </div>

        {/* Toast Notifications */}
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

        {/* Version History Sidebar */}
        {scheduleId && (
          <VersionHistorySidebar
            isOpen={isVersionHistoryOpen}
            onClose={() => setIsVersionHistoryOpen(false)}
            currentScheduleId={scheduleId}
          />
        )}
      </div>
    </SchedulingContext.Provider>
  );
}

// Header Component
interface HeaderProps {
  schedules: any[] | undefined;
  currentSchedule: any | undefined;
  setSelectedScheduleId: (id: string) => void;
}

function Header({ schedules, currentSchedule, setSelectedScheduleId }: HeaderProps) {
  const {
    viewMode,
    setViewMode,
    selectedDate,
    setSelectedDate,
    blockPainterActive,
    setBlockPainterActive,
    painterActivityType,
    setPainterActivityType,
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    handleCopyWeek,
    showToast,
    openVersionHistory,
  } = useSchedulingContext();

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        {/* Left: Title + Date */}
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Schedule</h1>

          {/* Schedule Selector */}
          {schedules && schedules.length > 1 && (
            <select
              value={currentSchedule?.id || ''}
              onChange={(e) => setSelectedScheduleId(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {schedules.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name}
                </option>
              ))}
            </select>
          )}

          {/* Date Navigation - Compact */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={handlePreviousDay}
              className="p-1.5 text-gray-700 hover:bg-gray-100 rounded"
              title="Previous day"
            >
              ←
            </button>

            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

            <button
              onClick={handleToday}
              className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Today
            </button>

            <button
              onClick={handleNextDay}
              className="p-1.5 text-gray-700 hover:bg-gray-100 rounded"
              title="Next day"
            >
              →
            </button>
          </div>

          {/* View Mode Switcher */}
          <div className="flex items-center bg-gray-100 rounded p-0.5">
            {(['DAILY', 'WEEKLY', 'MONTHLY'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  viewMode === mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {mode.charAt(0)}{mode.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Tools & Actions */}
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className={`p-1 rounded ${canUndo ? 'text-gray-700 hover:bg-white' : 'text-gray-400 cursor-not-allowed'}`}
              title="Undo"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className={`p-1 rounded ${canRedo ? 'text-gray-700 hover:bg-white' : 'text-gray-400 cursor-not-allowed'}`}
              title="Redo"
            >
              <Redo2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Quick Actions */}
          <button
            className="p-1.5 text-gray-700 hover:bg-gray-100 rounded"
            onClick={handleCopyWeek}
            title="Copy week"
          >
            <Copy className="h-4 w-4" />
          </button>

          <button
            className="p-1.5 text-purple-700 hover:bg-purple-50 rounded"
            onClick={() => showToast('Auto-fill feature coming soon', 'info')}
            title="Auto-fill gaps"
          >
            <Zap className="h-4 w-4" />
          </button>

          <button
            className="p-1.5 text-blue-700 hover:bg-blue-50 rounded"
            onClick={openVersionHistory}
            title="Version History"
          >
            <History className="h-4 w-4" />
          </button>

          {/* Block Painter */}
          {blockPainterActive ? (
            <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-1">
              <Paintbrush className="h-3.5 w-3.5 text-blue-600" />
              <div className="flex items-center gap-0.5">
                {(['PHONE', 'CHAT', 'EMAIL', 'BREAK', 'MEAL'] as ActivityType[]).map((activity) => (
                  <button
                    key={activity}
                    onClick={() => setPainterActivityType(activity)}
                    className={`px-1.5 py-0.5 text-xs rounded ${
                      painterActivityType === activity
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-blue-100'
                    }`}
                  >
                    {activity.substring(0, 2)}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setBlockPainterActive(false);
                  setPainterActivityType(null);
                }}
                className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded"
              >
                Exit
              </button>
            </div>
          ) : (
            <button
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => {
                setBlockPainterActive(true);
                setPainterActivityType('PHONE');
              }}
            >
              <Paintbrush className="h-3.5 w-3.5" />
              Paint
            </button>
          )}

          {/* Auto-Schedule */}
          <button
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => showToast('Auto-schedule feature coming soon', 'info')}
            title="Auto-schedule"
          >
            <Wand2 className="h-3.5 w-3.5" />
            Auto
          </button>

          {/* Publish */}
          <button
            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            onClick={() => showToast('Publish feature coming soon', 'info')}
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
}
